<template>
  <div class="page-content almacen-page">
    <div class="almacen-wrapper">
      <div class="almacen-card">
        <!-- Header -->
        <header class="entrada-header">
          <div>
            <p class="almacen-title">Órdenes de recolección</p>
            <p class="almacen-subtitle">
              Gestiona la recolección de equipos sobre órdenes de entrega ya finalizadas.
            </p>
          </div>

          <div class="entrada-header__actions">
            <div class="search-box">
              <input
                v-model="busqueda"
                type="text"
                placeholder="Buscar por folio o centro…"
              >
            </div>
          </div>
        </header>

        <!-- Filtros -->
        <section class="filtros-section">
          <div class="filtro-pills">
            <button
              v-for="f in filtros"
              :key="f.value"
              type="button"
              class="filtro-pill"
              :class="{ 'filtro-pill--active': filtroEstado === f.value }"
              @click="cambiarFiltro(f.value)"
            >
              {{ f.label }}
            </button>
          </div>
        </section>

        <!-- Tabla de órdenes -->
        <section>
          <!-- LOADING -->
          <div v-if="cargando" class="estado-centrado">
            <div class="spinner"></div>
            <p>Cargando órdenes de recolección…</p>
          </div>

          <!-- ERROR -->
          <div v-else-if="error" class="estado-centrado estado-error">
            <p>{{ error }}</p>
          </div>

          <!-- VACÍO -->
          <div
            v-else-if="!ordenesFiltradas.length"
            class="estado-centrado"
          >
            <p>No hay órdenes de recolección para el filtro seleccionado.</p>
          </div>

          <!-- TABLA -->
          <div v-else class="tabla-wrapper">
            <table class="tabla-ordenes">
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Centro</th>
                  <th>Fecha programada</th>
                  <th>Contacto</th>
                  <th>Unidades</th>
                  <th>Estado recolección</th>
                  <th class="col-acciones">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="orden in ordenesFiltradas"
                  :key="orden.id_orden"
                >
                  <td>
                    <span class="tabla-folio">
                      {{ formatoFolio(orden.folio) }}
                    </span>
                    <span class="tabla-token">
                      ID #{{ orden.id_orden }}
                    </span>
                  </td>
                  <td>
                    <div class="tabla-centro">
                      <div class="tabla-centro-nombre">
                        {{ orden.centro_nombre || '—' }}
                      </div>
                    </div>
                  </td>
                  <td>
                    {{ formatFecha(orden.fecha_programada) }}
                  </td>
                  <td>
                    <div class="tabla-contacto">
                      <div>{{ orden.contacto_nombre || '—' }}</div>
                      <div
                        v-if="orden.contacto_telefono"
                        class="tabla-contacto-extra"
                      >
                        {{ orden.contacto_telefono }}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="tabla-unidades">
                      <span>
                        Entregadas:
                        <strong>{{ orden.unidades_entregadas || 0 }}</strong>
                      </span>
                      <span>
                        Devueltas:
                        <strong>{{ orden.unidades_devueltas || 0 }}</strong>
                      </span>
                      <span>
                        Pendientes:
                        <strong>{{ orden.unidades_pendientes || 0 }}</strong>
                      </span>
                    </div>
                  </td>
                  <td>
                    <span
                      class="status-pill"
                      :class="orden.recoleccion_completa ? 'status-pill--recoleccion-completa' : 'status-pill--recoleccion-pendiente'"
                    >
                      {{ etiquetaRecoleccion(orden) }}
                    </span>
                  </td>
                  <td class="col-acciones">
                    <div class="acciones-stack">
                      <button
                        type="button"
                        class="ios-ghost-button tabla-btn"
                        @click="irAOrden(orden)"
                      >
                        👁 Ver / recolectar
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'OrdenesRecoleccion',

  data() {
    return {
      cargando: false,
      error: null,
      ordenes: [],
      filtroEstado: 'pendientes', // pendientes | completas | todas
      busqueda: '',
      filtros: [
        { value: 'pendientes', label: 'Pendientes' },
        { value: 'completas', label: 'Completas' },
        { value: 'todas', label: 'Todas' },
      ],
    };
  },

  computed: {
    ordenesFiltradas() {
      const term = this.busqueda.trim().toLowerCase();
      const base = this.ordenes || [];

      if (!term) {
        return base;
      }

      return base.filter((o) => {
        const folio = (o.folio || '').toString().toLowerCase();
        const centro = (o.centro_nombre || '').toLowerCase();
        return folio.includes(term) || centro.includes(term);
      });
    },
  },

  created() {
    this.cargarOrdenes();
  },

  methods: {
    async cargarOrdenes() {
      this.cargando = true;
      this.error = null;

      try {
        const { data } = await axios.get(
          '/api/almacen/ordenes-recoleccion/listar',
          {
            params: {
              filtro_estado: this.filtroEstado,
            },
          },
        );

        this.ordenes = data?.data?.ordenes || [];
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        this.error =
          err.response?.data?.message ||
          err.message ||
          'Ocurrió un error al cargar las órdenes de recolección.';
        this.ordenes = [];
      } finally {
        this.cargando = false;
      }
    },

    cambiarFiltro(filtro) {
      if (this.filtroEstado === filtro) return;
      this.filtroEstado = filtro;
      this.cargarOrdenes();
    },

    formatFecha(fecha) {
      if (!fecha) return 'Sin fecha';
      try {
        const d = new Date(fecha);
        if (Number.isNaN(d.getTime())) return fecha;
        return d.toLocaleDateString('es-MX', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
        });
      } catch (e) {
        return fecha;
      }
    },

    formatoFolio(folio) {
      if (!folio && folio !== 0) return 'SIN FOLIO';
      const n = parseInt(folio, 10);
      if (Number.isNaN(n)) return folio;
      return 'REC-' + String(n).padStart(4, '0');
    },

    etiquetaRecoleccion(orden) {
      const pendientes = Number(orden.unidades_pendientes || 0);
      if (orden.recoleccion_completa || pendientes <= 0) {
        return 'Recolección completa';
      }
      return 'Recolección pendiente';
    },

    irAOrden(orden) {
      this.$router.push({
        name: 'VerOrdenRecoleccion',
        params: { idOrden: orden.id_orden },
      });
    },
  },
};
</script>

<style scoped>
.almacen-page {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 1.5rem 1rem;
}

.almacen-wrapper {
  width: 100%;
  max-width: 1100px;
}

.almacen-card {
  border-radius: 1.8rem;
  padding: 1.5rem 1.4rem 1.2rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.35),
      rgba(15, 23, 42, 0.96)
    );
  backdrop-filter: blur(24px);
  border: 1px solid rgba(148, 163, 184, 0.6);
  box-shadow:
    0 18px 45px rgba(15, 23, 42, 0.9),
    0 0 0 1px rgba(15, 23, 42, 0.85);
}

.almacen-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #e5e7eb;
}

.almacen-subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
}

.entrada-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.1rem;
}

.entrada-header__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

/* Buscador */
.search-box input {
  background: radial-gradient(
      circle at top left,
      rgba(30, 41, 59, 0.96),
      rgba(15, 23, 42, 0.98)
    );
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.9);
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  color: #e5e7eb;
  outline: none;
}

.search-box input::placeholder {
  color: #64748b;
}

/* Filtros */
.filtros-section {
  margin-bottom: 0.7rem;
}

.filtro-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.filtro-pill {
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.9);
  color: #e5e7eb;
  font-size: 0.78rem;
  padding: 0.18rem 0.75rem;
  cursor: pointer;
}

.filtro-pill--active {
  background: radial-gradient(
    circle at 0 0,
    rgba(129, 140, 248, 0.7),
    rgba(37, 99, 235, 1)
  );
  border-color: rgba(129, 140, 248, 0.9);
}

/* Tabla */
.tabla-wrapper {
  border-radius: 1.1rem;
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: rgba(15, 23, 42, 0.96);
  padding: 0.4rem 0.5rem;
  overflow-x: auto;
}

.tabla-ordenes {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
  color: #e5e7eb;
}

.tabla-ordenes th,
.tabla-ordenes td {
  padding: 0.35rem 0.4rem;
  border-bottom: 1px solid rgba(30, 64, 175, 0.5);
  text-align: left;
}

.tabla-ordenes thead th {
  font-weight: 600;
  color: #cbd5f5;
  border-bottom: 1px solid rgba(148, 163, 184, 0.7);
}

.tabla-ordenes tbody tr:last-child td {
  border-bottom: none;
}

.col-acciones {
  width: 170px;
}

/* Acciones en columna */
.acciones-stack {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

/* Celda folio */
.tabla-folio {
  display: block;
  font-weight: 600;
}

.tabla-token {
  display: block;
  font-size: 0.68rem;
  color: #94a3b8;
}

/* Centro */
.tabla-centro-nombre {
  font-weight: 500;
}

/* Contacto */
.tabla-contacto-extra {
  font-size: 0.7rem;
  color: #94a3b8;
}

/* Unidades */
.tabla-unidades {
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
}

/* Status */
.status-pill {
  border-radius: 999px;
  padding: 0.1rem 0.6rem;
  font-size: 0.7rem;
  border: 1px solid rgba(148, 163, 184, 0.7);
  background: rgba(15, 23, 42, 0.96);
}

.status-pill--recoleccion-pendiente {
  border-color: rgba(234, 179, 8, 0.9);
}

.status-pill--recoleccion-completa {
  border-color: rgba(34, 197, 94, 0.9);
}

/* Botones iOS */
.ios-primary-button,
.ios-ghost-button {
  border-radius: 999px;
  padding: 0.45rem 1rem;
  font-size: 0.8rem;
  border: 1px solid transparent;
  cursor: pointer;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    transform 0.08s ease,
    box-shadow 0.16s ease;
}

.ios-primary-button {
  background: radial-gradient(
    circle at 0 0,
    rgba(129, 140, 248, 0.7),
    rgba(37, 99, 235, 1)
  );
  border-color: rgba(129, 140, 248, 0.9);
  color: #e5e7eb;
  box-shadow: 0 14px 32px rgba(37, 99, 235, 0.7);
}

.ios-primary-button:hover {
  transform: translateY(-0.5px);
  box-shadow: 0 18px 38px rgba(37, 99, 235, 0.9);
}

.ios-ghost-button {
  background: rgba(15, 23, 42, 0.9);
  border-color: rgba(148, 163, 184, 0.7);
  color: #e5e7eb;
}

.ios-ghost-button:hover {
  background: rgba(15, 23, 42, 1);
}

.tabla-btn {
  padding-inline: 0.7rem;
}

/* Hints */
.entrada-hint {
  font-size: 0.8rem;
  color: #94a3b8;
}

/* ESTADOS CENTRADOS + SPINNER */
.estado-centrado {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
  justify-content: center;
  color: #a5b0de;
  font-size: 0.9rem;
}

.estado-error {
  color: #ff9ca6;
}

.spinner {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 3px solid rgba(115, 145, 255, 0.25);
  border-top-color: rgba(132, 165, 255, 0.95);
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .entrada-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .col-acciones {
    width: auto;
  }

  .acciones-stack {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.3rem;
  }
}
</style>
